import { useEffect, useRef, useCallback } from 'react';

const SCROLL_DURATION = 1100;
const WHEEL_SENSITIVITY = 2;
const LERP_FACTOR = 0.08;
const LERP_THRESHOLD = 0.5;
const DESKTOP_BREAKPOINT = 1024;

const easeOut = (t: number): number => Math.min(1, 1.001 - Math.pow(2, -10 * t));

type AnimationMode = 'eased' | 'lerp';

interface ScrollState {
    fromScroll: number;
    targetScroll: number;
    animStartTime: number;
    currentScroll: number;
    isAnimating: boolean;
    rafId: number;
    mode: AnimationMode;
}

interface UseSmoothScrollReturn {
    scrollTo: (target: HTMLElement | number, options?: { offset?: number }) => void;
}

const clampScroll = (value: number): number => {
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;

    return Math.max(0, Math.min(value, maxScroll));
};

const lerp = (start: number, end: number, factor: number): number => start + (end - start) * factor;

const useSmoothScroll = (): UseSmoothScrollReturn => {
    const stateRef = useRef<ScrollState>({
        fromScroll: 0,
        targetScroll: 0,
        animStartTime: 0,
        currentScroll: 0,
        isAnimating: false,
        rafId: 0,
        mode: 'eased',
    });
    const isActiveRef = useRef(false);
    const animateFnRef = useRef<() => void>(() => undefined);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;

        if (!isDesktop) {
            return;
        }

        isActiveRef.current = true;
        const state = stateRef.current;
        state.currentScroll = window.scrollY;
        state.targetScroll = window.scrollY;
        state.fromScroll = window.scrollY;

        const getEasedPosition = (): number => {
            if (!state.isAnimating || state.mode !== 'eased') {
                return state.currentScroll;
            }

            const elapsed = performance.now() - state.animStartTime;
            const progress = Math.min(elapsed / SCROLL_DURATION, 1);

            return state.fromScroll + (state.targetScroll - state.fromScroll) * easeOut(progress);
        };

        const animate = () => {
            if (state.mode === 'eased') {
                const elapsed = performance.now() - state.animStartTime;
                const progress = Math.min(elapsed / SCROLL_DURATION, 1);
                const eased = easeOut(progress);

                state.currentScroll = state.fromScroll + (state.targetScroll - state.fromScroll) * eased;
                window.scrollTo(0, state.currentScroll);

                if (progress >= 1) {
                    state.currentScroll = state.targetScroll;
                    state.isAnimating = false;

                    return;
                }
            } else {
                const diff = state.targetScroll - state.currentScroll;

                if (Math.abs(diff) < LERP_THRESHOLD) {
                    state.currentScroll = state.targetScroll;
                    window.scrollTo(0, state.currentScroll);
                    state.isAnimating = false;

                    return;
                }

                state.currentScroll = lerp(state.currentScroll, state.targetScroll, LERP_FACTOR);
                window.scrollTo(0, state.currentScroll);
            }

            state.rafId = requestAnimationFrame(animate);
        };

        animateFnRef.current = animate;

        const startAnimation = () => {
            if (!state.isAnimating) {
                state.isAnimating = true;
                state.rafId = requestAnimationFrame(animate);
            }
        };

        const beginEasedScroll = (delta: number) => {
            const currentPos = getEasedPosition();

            state.mode = 'eased';
            state.fromScroll = currentPos;
            state.currentScroll = currentPos;
            state.targetScroll = clampScroll(state.targetScroll + delta * WHEEL_SENSITIVITY);
            state.animStartTime = performance.now();
            startAnimation();
        };

        const onWheel = (event: WheelEvent) => {
            event.preventDefault();
            beginEasedScroll(event.deltaY);
        };

        const onKeyDown = (event: KeyboardEvent) => {
            const tag = (event.target as HTMLElement)?.tagName;
            if (
                tag === 'INPUT' ||
                tag === 'TEXTAREA' ||
                tag === 'SELECT' ||
                (event.target as HTMLElement)?.isContentEditable
            ) {
                return;
            }

            const step = window.innerHeight * 0.4;
            let delta = 0;

            switch (event.key) {
                case 'ArrowDown':
                    delta = 80;
                    break;
                case 'ArrowUp':
                    delta = -80;
                    break;
                case 'PageDown':
                    if (!event.shiftKey) {
                        delta = step;
                    } else {
                        delta = -step;
                    }
                    break;
                case 'PageUp':
                    delta = -step;
                    break;
                case 'Home':
                    state.mode = 'lerp';
                    state.targetScroll = 0;
                    state.currentScroll = getEasedPosition();
                    startAnimation();

                    return;
                case 'End':
                    state.mode = 'lerp';
                    state.targetScroll = document.documentElement.scrollHeight - window.innerHeight;
                    state.currentScroll = getEasedPosition();
                    startAnimation();

                    return;
                default:
                    return;
            }

            event.preventDefault();
            beginEasedScroll(delta);
        };

        const onScroll = () => {
            if (!state.isAnimating) {
                const actual = window.scrollY;

                state.currentScroll = actual;
                state.targetScroll = actual;
                state.fromScroll = actual;
            }
        };

        window.addEventListener('wheel', onWheel, { passive: false });
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('scroll', onScroll, { passive: true });

        return () => {
            isActiveRef.current = false;
            cancelAnimationFrame(state.rafId);
            state.isAnimating = false;
            window.removeEventListener('wheel', onWheel);
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('scroll', onScroll);
        };
    }, []);

    const scrollTo = useCallback((target: HTMLElement | number, options?: { offset?: number }) => {
        const state = stateRef.current;
        const offset = options?.offset ?? 0;

        let targetPosition: number;

        if (typeof target === 'number') {
            targetPosition = target + offset;
        } else {
            const rect = target.getBoundingClientRect();
            targetPosition = rect.top + window.scrollY + offset;
        }

        if (isActiveRef.current) {
            state.mode = 'eased';
            state.fromScroll = window.scrollY;
            state.currentScroll = window.scrollY;
            state.targetScroll = clampScroll(targetPosition);
            state.animStartTime = performance.now();

            if (!state.isAnimating) {
                state.isAnimating = true;
                state.rafId = requestAnimationFrame(animateFnRef.current);
            }
        } else {
            window.scrollTo({ top: targetPosition, behavior: 'smooth' });
        }
    }, []);

    return { scrollTo };
};

export default useSmoothScroll;
